// Backend
import React from 'react';

// Frontend
import { Grid, Cell } from 'react-foundation';

// Functionality
import { Link } from 'react-router-dom';

// Components
import {
  Header,
  FlashMessage,
  ProgressBar,
} from '../../constants/SharedComponents';
import QuestionCategory from '../../components/QuestionCategory';

// Demo data for conversation topics - matching the demo categories from DemoCategoriesResults
const demoSelectedCategories = [
  {
    id: '4af58c57-cbe4-499e-99ec-fb33c45c1b98',
    name: 'Change',
    description: 'Change relates about how workplace changes are managed. Change could include major organisations-level changes, such as restructures, merger, acquisition, new physical workspace, or new management.',
    priorityRank: 1,
    categoryType: 0,
    color: '#1e7e34'
  },
  {
    id: '7185abba-d609-4071-b6b9-6e199c475e38', 
    name: 'Relationships',
    description: 'Relationships refers to the quality of workplace relationships. Ideally, people should be working in a positive work atmosphere in which conflict is properly resolved and unacceptable behaviour is not tolerated.',
    priorityRank: 2,
    categoryType: 0,
    color: '#dc3545'
  },
  {
    id: 'ee048b5c-97b2-4f1e-bf90-0da6169729f8',
    name: 'Control',
    description: 'Control refers to the extent to which you can decide things about your work. Control can have various forms: it can be whether you have discretion on your working patterns, whether you can decide how to do things in your work.',
    priorityRank: 3,
    categoryType: 0,
    color: '#fd7e14'
  }
];

// Demo stress level data
const demoQuestionScores = {
  questionText: 'How stressful do you find your job?',
  optionText: 'Somewhat Stressful',
  score: 3
};

// Mock question results content function
const demoQuestionResultsContent = (categoryId, field) => {
  const demoResponses = {
    '4af58c57-cbe4-499e-99ec-fb33c45c1b98': { // Change
      why: 'Constant changes in project requirements make it difficult to plan and complete work effectively.',
      impact: 'Feeling overwhelmed and uncertain about priorities, leading to increased stress and reduced job satisfaction.',
      notes: 'Would like to discuss better communication about upcoming changes and more structured change management processes.'
    },
    '7185abba-d609-4071-b6b9-6e199c475e38': { // Relationships
      why: 'Limited collaboration opportunities and some interpersonal conflicts within the team.',
      impact: 'Feeling isolated and struggling to get support when needed for complex tasks.',
      notes: 'Interested in team building activities and clearer communication channels.'
    },
    'ee048b5c-97b2-4f1e-bf90-0da6169729f8': { // Control
      why: 'Few opportunities to make decisions about how work is completed or prioritized.',
      impact: 'Feeling micromanaged and unable to use skills and experience effectively.',
      notes: 'Would appreciate more autonomy in day-to-day work and involvement in planning decisions.'
    }
  };

  return demoResponses[categoryId] ? demoResponses[categoryId][field] : '';
};



export function DemoConversationsManagersShow() {
  // Demo state - no loading required
  const [flashMessageObject, setFlashMessageObject] = React.useState({});
  const [loading] = React.useState(false);
  const [error] = React.useState(null);
  const [currentModal, setCurrentModal] = React.useState('');

  // Demo data shows that team member chose to share their information
  const canShareWithManager = true;
  const questionScores = demoQuestionScores;
  const selectedCategories = demoSelectedCategories;

  // Handle demo support information button click
  const handleSupportInfoClick = (e) => {
    e.preventDefault();
    
    // Show a demo notification
    setFlashMessageObject({
      message: 'Demo Mode: In the real application, this would take you to the Knowledge Hub where you can access manager preparation guides, training materials, and support resources.',
      className: 'notice'
    });
    
    // Scroll to top to show the flash message
    window.scrollTo(0, 0);
  };

  function openModal(event) {
    event.preventDefault();
    const modalId = event.target.getAttribute('data-modal-id');
    setCurrentModal(modalId);
  }

  function closeModal() {
    setCurrentModal('');
  }

  function handleChange() {
    // Demo version - no actual form changes needed
  }
  
  return (
    <>
      {/* Global styles for demo category colors - matching DemoCategoriesResults */}
      <style>
        {`
          .category-form-field__title--change {
            color: var(--color--green-turquoise--800) !important;
          }
          .category-form-field__title--relationships {
            color: var(--color--green-alternative--500) !important;
          }
          .category-form-field__title--control {
            color: var(--color--blue--300) !important;
          }
        `}
      </style>
      
      <ProgressBar loading={loading} />
      <Header />
      <FlashMessage object={flashMessageObject} columnWrap={true} />
      
      {error && (
        <div className="grid-container">
          <Grid className="grid-padding-x">
            <Cell small={12}>
              <div className="page-content">
                <h1 className="page-content__title">Error</h1>
                <p>{error}</p>
              </div>
            </Cell>
          </Grid>
        </div>
      )}

      {canShareWithManager && (
        <div className="grid-container">
          <Grid className="grid-padding-x align-justify">
            <Cell small={12}>
              <div className="page-content">
                  <div className="page-content__header">
                    <Link
                      to="/demo/conversations"
                      className="page-content__button--medium page-content__button--back mb-3"
                      id="page-navigation-button--go-back"
                    >
                      Back to Conversations
                    </Link>
                  </div>

                  <h1 className="page-content__title">Manager Prep</h1>

                  <div className="page-content__styles">
                    <p>
                      The Team Member has completed their prep for this conversation and has chosen
                      to share it with you
                    </p>

                    <Link 
                      to="/resources" 
                      className="page-content__button"
                      onClick={handleSupportInfoClick}
                    >
                      View Manager Prep Support Information
                    </Link>

                    <hr />

                    {/* Demo Notice */}
                    <div style={{
                      backgroundColor: '#e8f5e8',
                      padding: '15px',
                      borderRadius: '6px',
                      marginBottom: '20px',
                      border: '1px solid #d4edda'
                    }}>
                      <p style={{ 
                        fontSize: '14px',
                        color: '#155724',
                        marginBottom: '10px',
                        fontWeight: '500'
                      }}>
                        📋 <strong>Demo Mode:</strong> This is a demonstration of the Manager Prep page. 
                        In the real application, this would show the actual responses from your team member.
                      </p>
                      <p style={{ 
                        fontSize: '14px',
                        color: '#155724',
                        marginBottom: '0',
                        fontWeight: '500'
                      }}>
                        🎯 <strong>Content:</strong> The topics and responses shown below are examples to demonstrate the conversation preparation process.
                      </p>
                    </div>

                    {questionScores && (
                      <>
                        <h3 className="page-content__subtitle">{questionScores.questionText}</h3>

                        <div className="page-content__styles">
                          <p>
                            <b>{questionScores.optionText}</b>
                          </p>

                          {(questionScores.optionText === 'Extremely Stressful' ||
                            questionScores.optionText === 'Very Stressful') && (
                            <>
                              <p>
                                <strong>
                                  **IMPORTANT NOTE:** Your team member reports feeling very or
                                  extremely stressed in their job.
                                </strong>
                              </p>
                              <p>
                                This indicates their current situation requires urgent consideration
                                and we recommend that you expedite their conversation to address
                                their current challenges.
                              </p>
                              <p>
                                We also recommend you go through your organisational policies to
                                guide you and consider if further action is needed. In the meantime,
                                you can also inform your team member about the other organisational
                                provisions offered (e.g., employee assistance program, counselling
                                sessions if available).
                              </p>

                              <p>
                                This response from your team member highlights greater urgency of
                                taking actions to mitigate the levels of stress. During the
                                conversation and the co-creation of actions, you can ask your team
                                member: what would be an easy/quick step to take for helping you
                                feel better?
                              </p>
                            </>
                          )}
                        </div>
                      </>
                    )}
                    
                    <h3 className="page-content__subtitle">Conversation Topics</h3>
                    <Grid className="grid-padding-x">
                      {selectedCategories?.map((questionCategory) => (
                        <Cell
                          small={12}
                          large={4}
                          key={'selected-category-' + questionCategory.id}
                        >
                            <QuestionCategory
                              afterOpenModal={() => {}}
                              categoryFormButtons={{ current: null }}
                              closeModal={closeModal}
                              currentModal={currentModal}
                              deselectCategory={() => {}}
                              handleChange={handleChange}
                              openModal={openModal}
                              questionCategory={questionCategory}
                              questionResultsContent={demoQuestionResultsContent}
                              selectCategory={() => {}}
                              selectedCategories={[]}
                              submissionUnlocked={false}
                              showSummary={true}
                              showNotes={false}
                            />
                          </Cell>
                        ))}
                    </Grid>
                  </div>
                </div>
              </Cell>
            </Grid>
          </div>
      )}
    </>
  );
}
